import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import api from "@/lib/api";
import { Menu, X, LogOut, ChevronDown, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/dashboard/NotificationBell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { ChangeOwnPasswordDialog } from "@/components/PasswordDialogs";

function SidebarContent({ nav, onNavigate }) {
  const { user } = useAuth();
  const [logoCfg, setLogoCfg] = useState(null);
  useEffect(() => { api.get("/website/logo").then(({ data }) => setLogoCfg(data)).catch(() => {}); }, []);
  return (
    <div className="flex flex-col h-full">
      <div className="min-h-16 py-3 flex items-center px-5 border-b border-white/10"><Logo dark mediaId={logoCfg?.versions?.admin || logoCfg?.versions?.light} size={logoCfg?.sizes?.admin_width} /></div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-0.5 custom-scroll">
        {nav.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate} data-testid={`side-${item.to}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-white/15 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
            <item.icon className="h-4.5 w-4.5 shrink-0" />{item.label}
            {item.badge > 0 && <span className="ml-auto h-5 min-w-5 px-1.5 rounded-full bg-accent text-white text-[11px] font-bold flex items-center justify-center">{item.badge}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 py-1.5">
          <Avatar className="h-8 w-8"><AvatarImage src={user?.photo} /><AvatarFallback className="bg-white/15 text-white text-xs">{(user?.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
          <div className="min-w-0"><p className="text-sm font-medium text-white truncate">{user?.name}</p><p className="text-xs text-white/50 truncate">{(user?.roles || []).map((r) => ({ doctor: "Médecin", secretary: "Secrétaire", admin: "Admin", super_admin: "Super admin" }[r])).join(", ")}</p></div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardLayout({ nav, portalSwitch }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/"); };
  const isDoctor = (user?.roles || []).includes("doctor");

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-sidebar text-sidebar-foreground fixed inset-y-0 left-0" style={{ background: "hsl(var(--sidebar-bg))" }}>
        <SidebarContent nav={nav} />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 p-0 border-0" style={{ background: "hsl(var(--sidebar-bg))" }}>
          <SidebarContent nav={nav} onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="h-16 bg-white border-b border-border sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6 gap-3">
          <button className="lg:hidden h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center" onClick={() => setOpen(true)} data-testid="dash-mobile-menu"><Menu className="h-5 w-5" /></button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            {portalSwitch}
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50" data-testid="user-menu">
                  <Avatar className="h-7 w-7"><AvatarImage src={user?.photo} /><AvatarFallback className="bg-primary/10 text-primary text-xs">{(user?.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate("/")}>Voir le site</DropdownMenuItem>
                {isDoctor && <DropdownMenuItem onClick={() => navigate("/espace/profil")}>Mon profil</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="change-password-menu"><KeyRound className="h-4 w-4 mr-2" />Changer mon mot de passe</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} className="text-rose-600" data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" />Déconnexion</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <ChangeOwnPasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
